import { uploadProjectFile } from '../../lib/projectUploads';

/** A file the user attached to a conversation.
 *
 *  An attachment used to have to FIT IN THE MESSAGE: an image was downscaled and base64-encoded into the
 *  request, a text file was inlined into the prompt between fences, and anything else was refused. That
 *  is where the four-type allow-list, the 5 MB image ceiling, the 256 kB text ceiling and the "binary —
 *  not inlinable" rejection all came from. None of them described a real limitation; they described the
 *  cost of carrying bytes inside a chat message.
 *
 *  The file is now uploaded into the user's project first, and the message carries its PATH. So there is
 *  nothing to encode, nothing to downscale and no type to admit or refuse — and the agent reads it with
 *  the file tools it already has, which sniff the type from the content, inline images as vision blocks
 *  and even render PDF pages. One read path instead of two.
 */
export interface Attachment {
  /** Name as stored, which may differ from the file's own name when it collided or was sanitized. */
  name: string;
  /** The absolute path the message hands the agent, IN THE PROJECT'S OWN FILESYSTEM: a daemon host path
   *  for a host project, and an absolute GUEST path for a managed one. The same `Read` opens either,
   *  because the turn runs in the project the file was uploaded into — which is exactly why the upload
   *  resolves its target from the conversation instead of from the client. */
  path: string;
  /** Path within the project, for display. */
  relative: string;
}

/** A file still on its way into the project, as the composer shows it until it attaches or fails. */
export interface PendingUpload {
  key: number;
  name: string;
  size: number;
  /** Bytes the server has durably accepted so far. */
  sent: number;
}

/** Why an attachment did not make it. There is no longer any such thing as an unsupported type or a file
 *  that is too big, so a failure here is either the transfer itself or a placement refusal — and those
 *  two need different words. A refusal like "ask an administrator to assign you one" is the only thing
 *  that tells the user what to DO, and collapsing it into a generic "upload failed" is what turns a
 *  one-line configuration fix into a day of guessing. */
export interface AttachRefusal {
  failed: true;
  /** The server's own explanation, when it sent a usable one. */
  reason?: string;
  /** The user cancelled it: nothing to report. */
  cancelled?: true;
}

/** Chat keeps its conversation-owned destination and attachment result shape. */
export function uploadAttachment(
  file: File,
  sessionId: string | undefined,
  opts: { onProgress?: (sent: number) => void; signal?: AbortSignal } = {},
): Promise<Attachment | AttachRefusal> {
  return uploadProjectFile(file, { sessionId }, opts);
}
